import React, { useState } from 'react';
import { Table, Typography, Button, InputNumber, Input, Space, Popconfirm, Tooltip, Modal, message } from 'antd';
import { FileExcelOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, PlusOutlined, SafetyCertificateOutlined, FileSearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { dateFromString } from '../../../../utils/dateFromString';
import ModalAgregarServicios from './modales/ModalAgregarServicios';
import { URL_BASE_ASISTENCIAS } from '../../../../constants/url';
import { getFile } from '../../../../utils/fetch';

const { Text, Title } = Typography;

const CAMPOS_INICIALES_SERVICIO = [
  { key: 'direccionOrigen', label: 'Origen' },
  { key: 'direccionDestino', label: 'Destino' },
  { key: 'cuentaEspecialGS', label: 'Cuenta especial GS' },
  { key: 'cuentaEspecialGNP', label: 'Cuenta especial GNP' },
  { key: 'costoTope', label: 'Costo Tope' }
];

const PlacaIcon = () => (
  <svg width="52" height="20" viewBox="0 0 52 20" style={{ cursor: 'pointer', display: 'block' }}>
    <rect x="0.5" y="0.5" width="51" height="19" rx="3" fill="white" stroke="#333" strokeWidth="1.2"/>
    <rect x="2" y="14.5" width="15" height="2.5" rx="0.5" fill="#006847"/>
    <rect x="35" y="14.5" width="15" height="2.5" rx="0.5" fill="#CE1126"/>
    <text x="26" y="12" fontSize="7" fontWeight="bold" textAnchor="middle" fill="#111" fontFamily="Arial, sans-serif">LFG-123-MX</text>
  </svg>
);

export default function TablaServicios({
  servicios,
  total,
  numeroServicios,
  onDescargarXLS,
  onUpdateItem,
  onAgregarServicio,
  onEliminarServicio,
  proveedorID,
  ordenPagoID,
  estatusOrdenPagoID,
  estaRechazada,
  documentosAsistencia = []
}) {
  const [editingKey, setEditingKey] = useState('');
  const [editingValues, setEditingValues] = useState({});
  const [modalServiciosVisible, setModalServiciosVisible] = useState(false);
  const [modalPlacaVisible, setModalPlacaVisible] = useState(false);
  const [urlPlacaModal, setUrlPlacaModal] = useState(null);
  const [placaTexto, setPlacaTexto] = useState('');
  const [modalDocumentosVisible, setModalDocumentosVisible] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [downloadingDocID, setDownloadingDocID] = useState(null);
  const [modalCamposVisible, setModalCamposVisible] = useState(false);
  const [selectedCampos, setSelectedCampos] = useState([]);

  const isEditing = (record) => record.ordenPagoItemID === editingKey;
  const esEditable = estatusOrdenPagoID === 1;

  const handleEdit = (record) => {
    setEditingKey(record.ordenPagoItemID);
    setEditingValues({
      ivaRetenido: record.ivaRetenido,
      isrRetenido: record.isrRetenido,
      sat: record.sat
    });
  };

  const handleSave = async (record) => {
    await onUpdateItem(record.ordenPagoItemID, editingValues);
    setEditingKey('');
    setEditingValues({});
  };

  const handleCancel = () => {
    setEditingKey('');
    setEditingValues({});
  };

  const handleOpenPlacaModal = (asistenciaID) => {
    const servicio = servicios.find(s => s.asistenciaID === asistenciaID);
    let placa = '';

    if (servicio) {
      Object.keys(servicio).forEach(key => {
        if (key.startsWith('campo') && servicio[key] && typeof servicio[key] === 'string' && servicio[key].includes('Placas:')) {
          placa = servicio[key].split('Placas:')[1]?.trim() || '';
        }
      });
    }

    setUrlPlacaModal(servicio?.urlPlaca ? `https://zeus-sigsa.eastus.cloudapp.azure.com/Asistencias/${asistenciaID}/${servicio.urlPlaca}` : null);
    setPlacaTexto(placa);
    setModalPlacaVisible(true);
  };

  const handleClosePlacaModal = () => {
    setModalPlacaVisible(false);
    setUrlPlacaModal(null);
    setPlacaTexto('');
  };

  const handleOpenDocumentosModal = (asistenciaID) => {
    const grupo = documentosAsistencia.find(g => g.asistenciaID === asistenciaID);
    setSelectedDocs(grupo?.documentos || []);
    setModalDocumentosVisible(true);
  };

  const handleCloseDocumentosModal = () => {
    setModalDocumentosVisible(false);
    setSelectedDocs([]);
  };

  const getCamposServicio = (record) => {
    const camposIniciales = CAMPOS_INICIALES_SERVICIO
      .map(campo => ({
        label: campo.label,
        value: String(record?.[campo.key] ?? '').trim()
      }))
      .filter(campo => campo.value);

    const camposDinamicos = Object.entries(record || {})
      .map(([key, value]) => ({ key, value, match: key.match(/^campo(\d+)$/i) }))
      .filter(campo => campo.match)
      .sort((a, b) => Number(a.match[1]) - Number(b.match[1]))
      .map(campo => ({ key: campo.match[1], value: campo.value }))
      .filter(campo => typeof campo.value === 'string' && campo.value.trim())
      .map((campo) => {
        const { key, value } = campo;
        const trimmedValue = value.trim();
        const separatorIndex = trimmedValue.indexOf(':');

        if (separatorIndex === -1) {
          return {
            label: `Campo ${key}`,
            value: trimmedValue
          };
        }

        return {
          label: trimmedValue.slice(0, separatorIndex).trim(),
          value: trimmedValue.slice(separatorIndex + 1).trim()
        };
      });

    return [...camposIniciales, ...camposDinamicos];
  };

  const handleOpenCamposModal = (record) => {
    setSelectedCampos(getCamposServicio(record));
    setModalCamposVisible(true);
  };

  const handleCloseCamposModal = () => {
    setModalCamposVisible(false);
    setSelectedCampos([]);
  };

  const handleDownloadDocumento = async (doc) => {
    const url = `${URL_BASE_ASISTENCIAS}/GetDocumento/${doc.asistenciaFileID}/Asistencia`;

    setDownloadingDocID(doc.asistenciaFileID);

    try {
      const result = await getFile(url, doc.nombre);

      if (result.code !== 0) {
        message.error('Error al descargar el documento');
      }
    } finally {
      setDownloadingDocID(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'asistenciaID',
      key: 'asistenciaID',
      width: 160,
      render: (text, record) => {
        const verificado = record.proveedorVerificado

        return (
          <Space size="small">
            <a
              href={`https://sigsa.escotel.mx/detalleAsistencia/${text}`}
              target='_blank'
              rel='noreferrer'
            >
              {text}
            </a>
            {verificado && (
              <Tooltip title="Proveedor verificado">
                <Text type="success"><SafetyCertificateOutlined /></Text>
              </Tooltip>
            )}
            {record.tienePlaca && (
              <Tooltip title="Imagen de la Placa">
                <span
                  style={{ transition: 'opacity 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  onClick={() => handleOpenPlacaModal(text)}
                >
                  <PlacaIcon />
                </span>
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (val) => dateFromString(val)?.soloFecha || val
    },
    {
      title: 'SAT',
      dataIndex: 'sat',
      key: 'sat',
      width: 100,
      align: 'center',
      render: (val, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Input
            value={editingValues.sat}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setEditingValues({ ...editingValues, sat: value });
              }
            }}
            size="small"
          />
        ) : val;
      }
    },
    {
      title: 'Servicio',
      dataIndex: 'servicio',
      key: 'servicio',
      width: 120
    },
    {
      title: 'Final - Tipo',
      dataIndex: 'estatusFinalNombre',
      key: 'estatusFinalNombre',
      width: 120,
      render: (_, record) => {
        const tipoServicio = record.localCarretero === "local" ? "L" : "C";
        return `${record.estatusFinalNombre || record.estatusFinal || ''} - ${tipoServicio}`;
      }
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      width: 100,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      key: 'iva',
      width: 80,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'IVAr',
      dataIndex: 'ivaRetenido',
      key: 'ivaRetenido',
      width: 80,
      align: 'right',
      render: (val, record) => {
        const editing = isEditing(record);
        return editing ? (
          <InputNumber
            value={editingValues.ivaRetenido}
            onChange={(value) => setEditingValues({ ...editingValues, ivaRetenido: value })}
            prefix="$"
            size="small"
            block
          />
        ) : formatCurrency(val);
      }
    },
    {
      title: 'ISRr',
      dataIndex: 'isrRetenido',
      key: 'isrRetenido',
      width: 80,
      align: 'right',
      render: (val, record) => {
        const editing = isEditing(record);
        return editing ? (
          <InputNumber
            value={editingValues.isrRetenido}
            onChange={(value) => setEditingValues({ ...editingValues, isrRetenido: value })}
            prefix="$"
            size="small"
            block
          />
        ) : formatCurrency(val);
      }
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'right',
      render: (val, record) => {
        const editing = isEditing(record);
        const ivaRetenidoVal = editing ? (editingValues.ivaRetenido || 0) : (record.ivaRetenido || 0);
        const isrRetenidoVal = editing ? (editingValues.isrRetenido || 0) : (record.isrRetenido || 0);
        const calculatedTotal = (record.monto || 0) + (record.iva || 0) - ivaRetenidoVal - isrRetenidoVal;
        return <strong>{formatCurrency(calculatedTotal)}</strong>;
      }
    }
  ];

  columns.push({
    title: 'Acciones',
    key: 'acciones',
    width: esEditable ? 190 : 100,
    align: 'center',
    fixed: 'right',
    render: (_, record) => {
      const editing = isEditing(record);
      return (
        <Space size="small">
          {esEditable && editing && (
            <>
              <Button
                type="link"
                icon={<SaveOutlined />}
                onClick={() => handleSave(record)}
                size="small"
              />
              <Button
                type="link"
                icon={<CloseOutlined />}
                onClick={handleCancel}
                size="small"
                danger
              />
            </>
          )}
          {esEditable && !editing && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
              />
              <Popconfirm
                title="¿Eliminar este servicio?"
                onConfirm={() => onEliminarServicio(record.ordenPagoItemID)}
                okText="Si, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                overlayClassName="popconfirm-rounded"
              >
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                />
              </Popconfirm>
            </>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Tooltip title="Ver campos">
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                onClick={() => handleOpenCamposModal(record)}
                size="small"
              />
            </Tooltip>
            {documentosAsistencia.some(g => g.asistenciaID === record.asistenciaID && g.documentos?.length > 0) && (
              <Tooltip title="Ver documentos">
                <Button
                  type="text"
                  icon={<FileSearchOutlined />}
                  onClick={() => handleOpenDocumentosModal(record.asistenciaID)}
                  size="small"
                />
              </Tooltip>
            )}
          </div>
        </Space>
      );
    }
  });

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Registros: {numeroServicios} &nbsp;&nbsp;&nbsp; Total: {formatCurrency(total)}
        </Text>
        <Space>
          {esEditable && (
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={() => setModalServiciosVisible(true)}
              disabled={estaRechazada}
            >
              Agregar Servicios
            </Button>
          )}
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={onDescargarXLS}
          >
            Exportar XLS
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={servicios}
        rowKey="ordenPagoItemID"
        pagination={false}
        scroll={{ x: 1200 }}
        size="small"
      />

      <ModalAgregarServicios
        visible={modalServiciosVisible}
        onClose={() => setModalServiciosVisible(false)}
        onAgregarServicio={onAgregarServicio}
      />

      <Modal
        title={placaTexto ? `Placas Registradas: ${placaTexto}` : 'Imagen de la Placa'}
        open={modalPlacaVisible}
        onCancel={handleClosePlacaModal}
        footer={null}
        width={800}
        centered
        className="pagos-v2-modal"
      >
        {urlPlacaModal && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={urlPlacaModal}
              alt="Placa"
              className="rounded-8"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="Documentos de Asistencia"
        open={modalDocumentosVisible}
        onCancel={handleCloseDocumentosModal}
        footer={null}
        width={700}
        centered
        className="pagos-v2-modal"
      >
        {selectedDocs.length === 0 ? (
          <Text type="secondary">No hay documentos disponibles</Text>
        ) : (
          selectedDocs.map((doc, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              <Button
                type="link"
                onClick={() => handleDownloadDocumento(doc)}
                loading={downloadingDocID === doc.asistenciaFileID}
                disabled={!!downloadingDocID}
                style={{ padding: 0 }}
              >
                {doc.nombre}
              </Button>
            </div>
          ))
        )}
      </Modal>

      <Modal
        title={<><InfoCircleOutlined className="campos-servicio-modal__title-icon" />Campos del servicio</>}
        open={modalCamposVisible}
        onCancel={handleCloseCamposModal}
        footer={null}
        width={700}
        centered
        className="pagos-v2-modal"
        wrapClassName="campos-servicio-modal"
      >
        <div className="campos-servicio-modal__intro">
          <Title level={3} className="campos-servicio-modal__heading">
            Detalle de campos
          </Title>
          <Text type="secondary" className="campos-servicio-modal__description">
            Informacion adicional enviada por la asistencia.
          </Text>
        </div>

        {selectedCampos.length === 0 ? (
          <div className="campos-servicio-modal__empty">
            <Text className="campos-servicio-modal__empty-title">Sin campos disponibles</Text>
            <Text type="secondary" className="campos-servicio-modal__empty-description">
              Esta asistencia no tiene campos adicionales para mostrar.
            </Text>
          </div>
        ) : (
          <div className="campos-servicio-modal__content">
            {selectedCampos.map((campo, idx) => (
              <div className="campos-servicio-modal__item" key={idx}>
                <Text className="campos-servicio-modal__label">{campo.label || `Campo ${idx + 1}`}</Text>
                <div className="campos-servicio-modal__value">{campo.value || '-'}</div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
